/**
 * Widget scope derivation helper (task 1.2).
 *
 * Transloco full keys carry their scope as the first dot-segment
 * (e.g. `o-auth.access-token.Token` → scope `o-auth`). This helper
 * collects every full key a widget set declares and returns the distinct
 * scopes those keys belong to.
 *
 * Keys that have no dot separator (global scope keys, e.g. `Cancel`) are
 * intentionally ignored — the global scope is always loaded eagerly and
 * needs no manual `transloco.load()` call.
 */

/**
 * Given an array of full Transloco i18n keys, returns the distinct scope
 * names implied by those keys (the first dot-segment of each key).
 *
 * Rules:
 * - A key with no dot (global scope) is ignored.
 * - The result contains each scope at most once (duplicates collapse).
 * - Hyphenated scope names (e.g. `o-auth`) are preserved exactly — only
 *   the `.` character acts as the segment separator, not `-`.
 *
 * @param keys - Array of full i18n keys (may contain global and scoped keys).
 * @returns Distinct scope names derived from the key prefixes.
 */
export function deriveScopes(keys: string[]): string[] {
  const scopeSet = new Set<string>();

  for (const key of keys) {
    const dotIndex = key.indexOf('.');
    if (dotIndex === -1) {
      // No dot → global scope key; skip
      continue;
    }
    const scope = key.slice(0, dotIndex);
    scopeSet.add(scope);
  }

  return Array.from(scopeSet);
}
